import * as React from "react";
import CompanyInfoGrabForm from "./CompanyInfoGrabForm";
import { getOnboardData, saveOnboardData } from "../../libs/onboard";
import { debounce, uniqueId } from "lodash";
import toast from "../../utils/toast";
import produce from "immer";
import styled from "styled-components";
import CenterDiv from "../core/CenterDiv";
import CoreDropzone from "../core/CoreDropzone";
import jfetch from "../../utils/jfetch";
import ProgressBar from "../core/ProgressBar";

const InlinePre = styled.span`
  background-color: #f5f5f5;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  color: #7e888e;
  display: inline;
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
  font-size: 13px;
  line-height: 1.42857;
  margin: 0 0 10px;
  overflow: auto;
  overflow-wrap: break-word;
  padding: 2px;
  word-break: break-all;
`;

interface IProps extends IOnboardStepProps {}

interface IState {
  infoGrabData?: ICompanyInfoGrab;
  loading: boolean;
  uploadingFiles: Array<{ name: string; uploadId: string }>;
  uploadedFiles: IJsonApiFile[];
}

class CompanyInfoGrab extends React.Component<IProps, IState> {
  state: IState = {
    infoGrabData: undefined,
    loading: true,
    uploadingFiles: [],
    uploadedFiles: [],
  };

  componentDidMount() {
    this.props.allowNextStep(true);
    this.props.onBeforeNextStep(() => {
      this.props.setStepComplete();
    });

    getOnboardData(this.props.org).then(resp => {
      this.setState(
        produce<IState>(draft => {
          if (resp.data) {
            if (resp.data.attributes.companyInfoGrab) {
              draft.infoGrabData = resp.data.attributes.companyInfoGrab;
            }
            if (resp.data.attributes.companyInfoGrabFiles) {
              draft.uploadedFiles = resp.data.attributes.companyInfoGrabFiles;
            }
          }
          draft.loading = false;
        })
      );
    });
  }

  onFileDrop = (acceptedFiles: File[]) => {
    const uploadId = uniqueId("upload_");

    this.setState(
      produce<IState>(draft => {
        draft.uploadingFiles = draft.uploadingFiles.concat(
          acceptedFiles.map(f => ({ name: f.name, uploadId }))
        );
      })
    );

    const data = new FormData();

    acceptedFiles.forEach(file =>
      data.append("company_info_grab_files[]", file)
    );

    jfetch
      .post<IJsonApiResponse<IOnboardDataRecord>>(
        `${this.props.org.links.url}/onboard_data/upload_info_files`,
        data
      )
      .then(resp => {
        this.setState(
          produce<IState>(draft => {
            draft.uploadingFiles = draft.uploadingFiles.filter(
              f => f.uploadId !== uploadId
            );
            draft.uploadedFiles =
              resp.data.attributes.companyInfoGrabFiles || [];
          })
        );
      });
  };

  onFormUpdate = debounce((prevState, state) => {
    saveOnboardData(this.props.org, { companyInfoGrab: state.values }).then(
      () => {
        toast.success("Info saved.");
      }
    );
  }, 1000);

  render() {
    const { org } = this.props;
    const { infoGrabData, loading, uploadingFiles, uploadedFiles } = this.state;
    return (
      <>
        <div className="panel panel-primary">
          <div className="panel-heading">
            Confidential Company Information Grab
          </div>
          <div className="panel-body">
            <p>
              In order for us to carry out our services effectively, we need
              some information about your company. Please complete the form
              below.
            </p>
            <p>
              Proceed to the next step when you've filled out as much
              information as possible.
            </p>
          </div>
        </div>

        <div className="panel panel-info">
          <div className="panel-heading">Documentation Uploads</div>
          <div className="panel-body">
            <p>
              If you have any of the following pieces of documentation, please
              upload them here.
            </p>
            <ul>
              <li>Organization Chart with Forecasting</li>
              <li>Previous Strategic Planning Documentation</li>
              <li>Current Performance Measures</li>
              <li>
                Company Logo in <InlinePre>.eps</InlinePre> Format
              </li>
            </ul>
            <CenterDiv>
              <CoreDropzone onDrop={this.onFileDrop}>
                Drop Files Here or Click to Upload
              </CoreDropzone>
            </CenterDiv>
            <CenterDiv>
              {uploadingFiles.length > 0 && (
                <p>
                  <strong>Uploading Files...</strong>
                </p>
              )}
              {uploadingFiles.map(f => (
                <ProgressBar
                  key={`${f.uploadId}_${f.name}`}
                  active={true}
                  progress={100}
                  striped={true}
                >
                  {f.name}
                </ProgressBar>
              ))}
            </CenterDiv>
            <hr />
            <CenterDiv>
              <p>
                <strong>Uploaded Files</strong>
              </p>
            </CenterDiv>
            <CenterDiv>
              {uploadedFiles.length > 0 ? (
                uploadedFiles.map(f => (
                  <div key={f.url}>
                    <a target={"_blank"} rel="noopener noreferrer" href={f.url}>
                      {f.name}
                    </a>
                  </div>
                ))
              ) : (
                <p>Nothing yet.</p>
              )}
            </CenterDiv>
          </div>
        </div>

        {!loading && (
          <CompanyInfoGrabForm
            infoGrabData={infoGrabData}
            onChange={this.onFormUpdate}
          />
        )}
      </>
    );
  }
}

export default CompanyInfoGrab;



// WEBPACK FOOTER //
// ./app/javascript/components/onboarding/CompanyInfoGrab.tsx